/**
 * The admin page's Privacy tab (L3), the pure half: what an org shares with the shared foundry knowledge, in words, and
 * the call that gives or withdraws each consent. The views are knowledge-sharing-panel.tsx.
 *
 * Both consents default off (the review of #1128, MEDIUM 1): nothing leaves an org until its admin switches it on.
 * Counts are a hypothesis's verdict in the org's data and a tried recommendation's effect, counts only; content is the
 * org's words, parameter names and recording text, words and names only. The reply carries NO count of what the org
 * shares (LOW 1): an admin could bind probe words and watch it, and a count that did not rise would say that some org's
 * name holds the token. The copy a customer reads is in the panel and holds only what the code keeps (MEDIUM 3).
 */
import { errorCodeOf, errorDetailOf, isUnauthorized, statusOf } from "@/lib/api/error";
import { knowledgeSharingApi, type KnowledgeSharing, type KnowledgeSharingSetting } from "@/lib/api/knowledge-candidates";

/** Gives (on) or withdraws (off) one consent through the org's own route; answers the org's part as it now stands. */
export function setSharing(setting: KnowledgeSharingSetting, on: boolean): Promise<KnowledgeSharing> {
  return on ? knowledgeSharingApi.enable(setting) : knowledgeSharingApi.disable(setting);
}

/** Why a change was refused, in plain words. */
export function sharingErrorText(err: unknown): string {
  switch (errorCodeOf(err)) {
    case "not_a_plant":
      return "Only an org with real plant data shares knowledge: a demo or a rehearsal org stands for no plant.";
    case "vendor_no_twin":
      return "This organisation's own admin changes what it shares.";
    case "product_not_enabled":
    case "product_suspended":
      return "Knowledge sharing is part of Foundry Twin, which is not active for this organisation.";
  }
  if (statusOf(err) === 403) return "Only org admins can change what the org shares.";
  if (isUnauthorized(err)) return "Your session expired: sign in again.";
  const detail = errorDetailOf(err);
  return detail ? `It was not saved: ${detail}` : "It was not saved. Try again.";
}
